import React, {
  FormEvent,
  useEffect,
  useState,
  ChangeEvent,
  useMemo,
} from "react";
import classNames from "classnames";
import { debounce } from "lodash-es";

import { i18nService } from "services";
import { Search } from "assets/icons";

const SEARCH_RATE_LIMIT = 500;

interface SearchFieldProps {
  id: string;
  searchType?: "onSubmit" | "onChange";
  initSearchValue?: string;
  isSubmitting?: boolean;
  onSubmit?: (data: any) => void;
  onChange?: (data: any) => void;
  menuOverlayOpen?: boolean;
  isOpen?: boolean;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputClick?: () => void;
  showFocusShadow?: boolean;
}

export const SearchField: React.FC<SearchFieldProps> = ({
  id,
  searchType = "onSubmit",
  initSearchValue,
  isSubmitting,
  onSubmit = () => {},
  onChange = () => {},
  menuOverlayOpen,
  isOpen = false,
  onInputFocus,
  onInputBlur,
  showFocusShadow = true,
  onInputClick,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState(initSearchValue || "");

  const debouncedOnSubmit = useMemo(
    () => debounce((data) => onSubmit(data), SEARCH_RATE_LIMIT),
    [onSubmit]
  );

  const debouncedOnChange = useMemo(
    () => debounce((data) => onChange(data), SEARCH_RATE_LIMIT),
    [onChange]
  );

  const handleSubmit = (
    event: FormEvent<HTMLFormElement> | null = null
  ): void => {
    if (event) {
      event.preventDefault();
    }

    if (
      searchType === "onSubmit" &&
      searchValue.length < 3 &&
      searchValue.length !== 0
    ) {
      setError(i18nService.copy["siteSearch.minChars"]);

      return;
    }

    setError(null);
    debouncedOnSubmit({ search: searchValue });
  };

  const handleInputChange = (event: ChangeEvent) => {
    const currentValue = (event.target as HTMLInputElement).value;
    setSearchValue(currentValue);

    if (searchType === "onSubmit") return;
    if (!onChange) return;

    debouncedOnChange({ search: currentValue });
  };

  useEffect(() => {
    if (!menuOverlayOpen) {
      setSearchValue("");
    }
  }, [menuOverlayOpen]);

  return (
    <form
      className={classNames("search-field", [
        isOpen && "is-open",
        showFocusShadow && "show-focus-shadow",
      ])}
      onSubmit={(event) => handleSubmit(event)}
    >
      <fieldset className="search-field__fieldset">
        {error && (
          <span className="form-message is-error is-animated" role="alert">
            {error}
          </span>
        )}
        <label className="search-field__label" htmlFor={id}>
          <span>{i18nService.copy["siteSearch.search"]}</span>
          <div className="search-field__input-wrapper">
            <input
              className={classNames("search-field__input", [
                error && "is-error",
              ])}
              id={id}
              role="combobox"
              name={id}
              type="search"
              aria-controls="search-results"
              aria-expanded={isOpen}
              placeholder={i18nService.copy["siteSearch.searchPlaceholder"]}
              value={searchValue}
              onChange={handleInputChange}
              autoComplete="off"
              onFocus={onInputFocus}
              onBlur={onInputBlur}
              onClick={onInputClick}
            />
            <button
              className="search-field__button"
              disabled={isSubmitting}
              tabIndex={-1}
              onClick={() => handleSubmit()}
            >
              <span className="visually-hidden">
                {i18nService.copy["siteSearch.search"]}
              </span>
              <Search />
            </button>
          </div>
        </label>
      </fieldset>
    </form>
  );
};
